import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { DocumentFile } from '../../api/app-types/document.app';

/**
 * Validator that requires at least one file to be uploaded
 */
export function requiredFile(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = control.value;

		if (!value) {
			return { requiredFile: { message: 'At least one file is required' } };
		}

		if (Array.isArray(value)) {
			return value.length > 0 ? null : { requiredFile: { message: 'At least one file is required' } };
		}

		return null;
	};
}

/**
 * Validator that checks minimum number of files for multiple file uploads
 */
export function minFiles(min: number): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = control.value;

		if (!value || !Array.isArray(value)) {
			return null; // Let requiredFile handle empty values
		}

		return value.length >= min
			? null
			: { minFiles: { min, actual: value.length, message: `At least ${min} files are required` } };
	};
}

/**
 * Validator that checks maximum number of files for multiple file uploads
 */
export function maxFiles(max: number): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = control.value;

		if (!value || !Array.isArray(value)) {
			return null;
		}

		return value.length <= max
			? null
			: { maxFiles: { max, actual: value.length, message: `Maximum ${max} files are allowed` } };
	};
}

/**
 * Validator that checks file types based on MIME type
 */
export function allowedFileTypes(allowedTypes: string[]): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = control.value as DocumentFile | DocumentFile[] | null;

		if (!value) {
			return null; // Let requiredFile handle empty values
		}

		const files = Array.isArray(value) ? value : [value];
		const invalidFiles: string[] = [];

		files.forEach((file) => {
			const isAllowed = allowedTypes.some((type) => {
				if (type.includes('/')) {
					// if allowed includes like images/* and its e.g. images/png it should also be allowed
					if (type.split('/')[1] === '*' && file.mimeType.startsWith(type.split('/')[0])) {
						return true;
					}
					// Exact MIME type match
					if (file.mimeType === type) {
						return true;
					}

					return {
						allowedFileTypes: {
							allowedTypes,
							invalidFiles,
							message: `Invalid file types: ${invalidFiles.join(', ')}. Allowed: ${allowedTypes.join(', ')}`,
						},
					};
				} else {
					// Extension match (assuming type starts with '.')
					return file.name.toLowerCase().endsWith(type.toLowerCase());
				}
			});

			if (!isAllowed) {
				invalidFiles.push(file.name);
			}
		});

		return invalidFiles.length === 0
			? null
			: {
					allowedFileTypes: {
						allowedTypes,
						invalidFiles,
						message: `Invalid file types: ${invalidFiles.join(', ')}. Allowed: ${allowedTypes.join(', ')}`,
					},
				};
	};
}

/**
 * Validator that checks for duplicate file names
 */
export function noDuplicateFiles(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = control.value as DocumentFile | DocumentFile[] | null;

		if (!value || !Array.isArray(value)) {
			return null;
		}

		const fileNames = value.map((file) => file.name.toLowerCase());
		const duplicates = fileNames.filter((name, index) => fileNames.indexOf(name) !== index);

		return duplicates.length === 0
			? null
			: {
					noDuplicateFiles: {
						duplicates: [...new Set(duplicates)],
						message: `Duplicate files found: ${[...new Set(duplicates)].join(', ')}`,
					},
				};
	};
}

/**
 * Helper function to get validation error messages
 */
export function getValidationErrorMessage(errors: ValidationErrors): string {
	const errorKey = Object.keys(errors)[0];
	const errorValue = errors[errorKey];

	if (errorValue && typeof errorValue === 'object' && 'message' in errorValue) {
		return errorValue.message;
	}

	// Fallback messages
	switch (errorKey) {
		case 'required':
			return 'This field is required';
		case 'requiredFile':
			return 'At least one file is required';
		case 'minFiles':
			return `At least ${errorValue.min} files are required`;
		case 'maxFiles':
			return `Maximum ${errorValue.max} files are allowed`;
		case 'allowedFileTypes':
			return `Invalid file type. Allowed: ${errorValue.allowedTypes?.join(', ')}`;
		case 'noDuplicateFiles':
			return 'Duplicate files are not allowed';
		default:
			return 'Invalid input';
	}
}

/**
 * Validator for max allowed size per file
 */
export function maxFileSize(max: number): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = control.value as DocumentFile | DocumentFile[] | null;

		if (!value) {
			return null; // Let requiredFile handle empty values
		}

		const files = Array.isArray(value) ? value : [value];
		const oversizedFiles = files.filter((file) => file.size > max);

		return oversizedFiles.length === 0
			? null
			: {
					maxFileSize: {
						max,
						actual: oversizedFiles.map((file) => file.name),
						message: `File size exceeds ${max} bytes: ${oversizedFiles.map((file) => file.name).join(', ')}`,
					},
				};
	};
}
